/**
 * Trace Format Scene Loader Plugin Registration
 *
 * Registers ISceneLoaderPluginAsync implementations for:
 * - .trace  - Legacy Trace format (ZIP with scene.json)
 * - .glz    - Trace bundle (ZIP with scene.gltf at the root, spec §4)
 * - .tracex - the bundle's retired name; read-only (spec §5)
 * - .glxf   - Legacy standalone scene document (JSON referencing external files)
 * - .real   - Adobe Aero format (ZIP with SCENE_JSON)
 *
 * Usage:
 *   import { registerTracePlugins } from './loaders/plugins/registerPlugins';
 *   registerTracePlugins();
 *
 * Then use standard Babylon.js loading:
 *   await AppendSceneAsync('scene.glz', scene);
 *   await ImportMeshAsync('model.trace', scene);
 */

import { RegisterSceneLoaderPlugin } from "@babylonjs/core/Loading/sceneLoader";

// Side-effect imports to register Babylon.js loaders
import "@babylonjs/loaders/SPLAT"; // Registers .splat, .ply, .spz, .sog loaders

import { TraceSceneLoaderPlugin } from './TraceSceneLoaderPlugin';
import { BundleSceneLoaderPlugin } from './BundleSceneLoaderPlugin';
import { GltfSceneLoaderPlugin } from './GltfSceneLoaderPlugin';
import { AeroSceneLoaderPlugin } from './AeroSceneLoaderPlugin';
import { SPZLoaderPlugin } from './SPZLoaderPlugin';

// Track registration state
let _registered = false;

/**
 * Register all Trace format scene loader plugins.
 * Uses plugin factories for lazy instantiation.
 *
 * Safe to call multiple times - subsequent calls are no-ops.
 */
export function registerTracePlugins(): void {
    if (_registered) {
        return;
    }

    // Register .trace plugin (legacy format)
    RegisterSceneLoaderPlugin({
        name: "trace",
        extensions: { ".trace": { isBinary: true } },
        createPlugin: () => new TraceSceneLoaderPlugin(),
    });

    // Register the bundle plugin (spec §4). `.tracex` is the same container
    // under its retired name and stays readable (spec §5, #816).
    RegisterSceneLoaderPlugin({
        name: "bundle",
        extensions: {
            ".glz": { isBinary: true },
            ".tracex": { isBinary: true },
        },
        createPlugin: () => new BundleSceneLoaderPlugin(),
    });

    // Register .glxf plugin (legacy standalone scene document).
    //
    // `.gltf`/`.glb` are deliberately NOT registered here: Babylon owns those
    // extensions, and a scene document reaches this plugin through
    // TraceScene's content sniff, never through Babylon's dispatch.
    RegisterSceneLoaderPlugin({
        name: "glxf",
        extensions: { ".glxf": { isBinary: false } },
        createPlugin: () => new GltfSceneLoaderPlugin(),
    });

    // Register .real plugin (Adobe Aero)
    RegisterSceneLoaderPlugin({
        name: "aero",
        extensions: { ".real": { isBinary: true } },
        createPlugin: () => new AeroSceneLoaderPlugin(),
    });

    // Override Babylon's built-in .spz handler with our custom loader
    // that supports both gzip and zstd envelopes, with proper error handling.
    // Must be registered AFTER the SPLAT side-effect import so it overwrites.
    RegisterSceneLoaderPlugin({
        name: "spz",
        extensions: { ".spz": { isBinary: true } },
        createPlugin: () => new SPZLoaderPlugin(),
    });

    _registered = true;
}

/**
 * Check if Trace plugins have been registered.
 */
export function areTracePluginsRegistered(): boolean {
    return _registered;
}
